"use client";
import { useCallback, useEffect, useState } from "react";

import { useScriptsContext } from "@stadium/hooks/lib/use-scripts";
import { asyncLoadAndAppend } from "@stadium/helpers/src/script-loader";

import { SCRIPTS } from "@stadium/constants/scripts";

const DEFAULT_OBJ = {};

const useScript = (scriptId: string, { lazy, onLoad } = { lazy: false, onLoad: () => null }) => {
  const { [scriptId]: currentScriptData, setScript, loadingRefs } = useScriptsContext();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(loadingRefs.current[scriptId]);

  const scriptLoader = useCallback(
    async ({ force = false } = DEFAULT_OBJ) => {
      if (!force && currentScriptData) return currentScriptData;

      loadingRefs.current[scriptId] = true;
      setLoading(true);
      try {
        const { attributes, url: src } = SCRIPTS[scriptId] || DEFAULT_OBJ;

        await asyncLoadAndAppend({
          elementType: "script",
          attributes,
          tagOptions: { src, onLoad },
        });
        const instance = await SCRIPTS[scriptId].getInstance();
        const scriptData = {
          instance,
          url: SCRIPTS[scriptId].url,
        };
        setScript(scriptId, scriptData);
        onLoad?.();

        loadingRefs.current[scriptId] = false;
        setLoading(false);

        return scriptData;
      } catch (e) {
        setError(e);
        loadingRefs.current[scriptId] = false;
        setLoading(false);

        throw e;
      }
    },
    [scriptId, currentScriptData],
  );

  useEffect(() => {
    if (loadingRefs.current[scriptId]) return;
    if (!lazy && !currentScriptData) {
      scriptLoader();
    }
  }, [lazy, currentScriptData]);

  return {
    load: scriptLoader,
    data: currentScriptData,
    loading: loadingRefs.current[scriptId] || loading,
    error,
  };
};

export default useScript;
